import { useCart, useShop } from "@shopify/hydrogen-react";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useConsent } from "../consent/ConsentProvider";
import {
  SHOPIFY_ADD_TO_CART_EVENT,
  getAnalyticsPageType,
  sendAddToCartAnalytics,
  sendPageViewAnalytics,
} from "./shopifyAnalytics";
import type { AddToCartAnalyticsDetail } from "./shopifyAnalytics";

let lastTrackedPageUrl: string | null = null;

export function ShopifyAnalyticsTracker() {
  const location = useLocation();
  const consent = useConsent();
  const { storeDomain, languageIsoCode } = useShop();
  const { id: cartId, totalQuantity } = useCart();
  const pendingAddToCartEvents = useRef<AddToCartAnalyticsDetail[]>([]);
  const previousTotalQuantity = useRef(totalQuantity ?? 0);

  useEffect(() => {
    const handleAddToCart = (event: Event) => {
      const detail = (event as CustomEvent<AddToCartAnalyticsDetail>).detail;
      pendingAddToCartEvents.current.push(detail);
    };

    window.addEventListener(SHOPIFY_ADD_TO_CART_EVENT, handleAddToCart);

    return () => {
      window.removeEventListener(SHOPIFY_ADD_TO_CART_EVENT, handleAddToCart);
    };
  }, []);

  useEffect(() => {
    if (!consent.analyticsAllowed) {
      return;
    }

    const currentUrl = window.location.href;
    if (lastTrackedPageUrl === currentUrl) {
      return;
    }

    lastTrackedPageUrl = currentUrl;

    void sendPageViewAnalytics({
      consent,
      storeDomain,
      language: languageIsoCode,
      pageType: getAnalyticsPageType(location.pathname),
    });
  }, [consent, languageIsoCode, location.pathname, storeDomain]);

  useEffect(() => {
    const currentTotalQuantity = totalQuantity ?? 0;
    const didCartGrow = currentTotalQuantity > previousTotalQuantity.current;

    previousTotalQuantity.current = currentTotalQuantity;

    if (!consent.analyticsAllowed || !cartId || !didCartGrow) {
      return;
    }

    const pendingEvent = pendingAddToCartEvents.current.shift();
    if (!pendingEvent) {
      return;
    }

    void sendAddToCartAnalytics({
      consent,
      storeDomain,
      language: languageIsoCode,
      cartId,
      products: pendingEvent.products,
      totalValue: pendingEvent.totalValue,
    });
  }, [cartId, consent, languageIsoCode, storeDomain, totalQuantity]);

  return null;
}
